/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import {getMode, getSetting, navigate} from '@dvrd/dvr-controls';

/**
 * Initiates the Bol.com OAuth authorization flow by redirecting the browser to
 * Bol's login page. After the user authenticates, Bol will redirect back to
 * `https://app.bolmate.nl/oauth/bol` with an authorization code.
 *
 * @param fakeFlow - When `true` and the app is not running in production, skips
 * the real Bol login and navigates straight to the local `/oauth/bol` route.
 * Useful for development and testing without going through Bol's servers.
 * @param oauthState - Optional value forwarded as the OAuth `state` parameter.
 * Bol echoes this back to the redirect URI unchanged, so it can be used to pass
 * directives to the app when the user returns from the OAuth flow — for example,
 * a path to navigate to or a flag indicating which action triggered the connect.
 * The value should be an unrecognizable string (like a UUID) because Bol expects
 * it to be a XSRF token.
 * If omitted, falls back to the `oauthState` setting.
 * @throws If the `bolOauthClientId` setting is not configured.
 */
export function onConnectBolOauth(fakeFlow: boolean = false, oauthState?: string) {
    if (getMode() !== 'prod' && fakeFlow) return navigate('/oauth/bol');
    const clientId = getSetting('bolOauthClientId');
    if (!clientId) throw new Error('Bol OAuth client id is not set');
    if (!oauthState) oauthState = getSetting('oauthState');
    const redirectUri = encodeURIComponent('https://app.bolmate.nl/oauth/bol');
    let uri = `https://login.bol.com/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&prompt=login`;
    if (oauthState) uri += `&state=${oauthState}`;
    window.location.assign(uri);
}
